import React, { useEffect, useRef, useState } from 'react';
import { Card, Loading, Tag, Button, Toast, ActionSheet } from 'antd-mobile';
import {
  AddOutline,
  DeleteOutline,
  FileOutline,
  SetOutline,
  InformationCircleOutline,
  HistogramOutline,
  PicturesOutline,
} from 'antd-mobile-icons';
import { useQuery } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { mobileApi } from '../api';
import {
  T,
  cardStyle,
  SectionCard,
  SubBlock,
  CellField,
  CellInput,
  cellDateStyle,
  ChoiceBlocks,
} from '../theme';

function EquipmentNameSearch({ value, onManualChange, onPick, onBlur }) {
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const timerRef = useRef(null);

  const search = (q) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (!q || q.trim().length < 2) {
      setResults([]);
      setOpen(false);
      return;
    }
    const query = q.trim();
    timerRef.current = setTimeout(async () => {
      try {
        const resp = await mobileApi.goszadanieGoEquipment(query);
        const items = resp?.data?.items || [];
        setResults(items);
        setOpen(items.length > 0);
      } catch (e) {
        // справочник недоступен — остаётся ручной ввод
      }
    }, 300);
  };

  return (
    <div style={{ position: 'relative' }}>
      <CellInput
        value={value || ''}
        onChange={(v) => {
          onManualChange(v);
          search(v);
        }}
        onBlur={() => {
          setTimeout(() => setOpen(false), 200);
          if (onBlur) onBlur();
        }}
        enterKeyHint="done"
        placeholder="Начните вводить — подбор из реестра СИиИО"
        clearable
      />
      {open && (
        <div
          style={{
            position: 'absolute',
            zIndex: 30,
            top: '100%',
            left: 0,
            right: 0,
            background: '#fff',
            border: `1px solid ${T.inputBorder}`,
            borderRadius: 10,
            maxHeight: 240,
            overflowY: 'auto',
            boxShadow: '0 6px 16px rgba(0,0,0,0.15)',
          }}
        >
          {results.map((item) => (
            <div
              key={item.id}
              onMouseDown={(e) => {
                e.preventDefault();
                setOpen(false);
                onPick(item);
              }}
              style={{ padding: '10px 12px', borderBottom: `1px solid ${T.divider}` }}
            >
              <div style={{ fontSize: 13, color: T.text }}>{item.name}</div>
              <div style={{ fontSize: 11, color: T.hint }}>
                {[item.serial_number, item.inventory_number].filter(Boolean).join(' / ') || '—'}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TestCodePage() {
  const { taskId, index } = useParams();
  const idx = parseInt(index, 10);
  const navigate = useNavigate();
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);
  const dirtyRef = useRef(false);
  const changeRef = useRef(0);

  const taskQuery = useQuery(
    ['goszadanieGoTask', taskId],
    () => mobileApi.goszadanieGoTask(taskId),
    {
      onError: (err) => {
        const detail = err?.response?.data?.detail || 'Не удалось загрузить задание';
        Toast.show({ icon: 'fail', content: String(detail) });
      },
    }
  );
  const checklistsQuery = useQuery(
    'goszadanieGoChecklists',
    () => mobileApi.goszadanieGoChecklists(),
    {
      onError: () => Toast.show({ icon: 'fail', content: 'Не удалось загрузить чек-листы' }),
    }
  );

  const task = taskQuery.data?.data;
  const checklists = checklistsQuery.data?.data || {};
  const code = task?.work_codes?.[idx];
  const schema = code ? checklists[code.code] : null;

  useEffect(() => {
    if (code) {
      setValues(code.data || {});
    }
  }, [code]);

  const setField = (key, val) => {
    dirtyRef.current = true;
    changeRef.current += 1;
    setValues((prev) => ({ ...prev, [key]: val }));
  };

  const saveCode = async (done, dataOverride) => {
    setSaving(true);
    try {
      await mobileApi.goszadanieGoSaveCode(taskId, idx, {
        data: dataOverride || values,
        done,
      });
      Toast.show({ icon: 'success', content: 'Сохранено' });
      return true;
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Ошибка сохранения';
      Toast.show({ icon: 'fail', content: String(detail) });
      return false;
    } finally {
      setSaving(false);
    }
  };

  const flushSave = () => {
    if (!dirtyRef.current) return;
    const at = changeRef.current;
    saveCode(code.done || false).then((ok) => {
      if (ok && changeRef.current === at) dirtyRef.current = false;
    });
  };

  const setFieldAndSave = (key, val) => {
    dirtyRef.current = true;
    changeRef.current += 1;
    const next = { ...values, [key]: val };
    setValues(next);
    const at = changeRef.current;
    saveCode(code.done || false, next).then((ok) => {
      if (ok && changeRef.current === at) dirtyRef.current = false;
    });
  };

  const blurInput = (e) => {
    if (e?.target?.blur) e.target.blur();
  };

  const handleMarkDone = async () => {
    const ok = await saveCode(true);
    if (ok) navigate(`/test/${taskId}`);
  };

  const handleUnmarkDone = async () => {
    const ok = await saveCode(false);
    if (ok) navigate(`/test/${taskId}`);
  };

  const equipment = Array.isArray(values.equipment) ? values.equipment : [];
  const setEquipment = (list) => setField('equipment', list);
  const setEquipmentAndSave = (list) => setFieldAndSave('equipment', list);
  const addEquipment = () =>
    setEquipmentAndSave([...equipment, { name: '', number: '', doc_number: '', valid_until: '' }]);
  const updateEquipment = (i, key, val) =>
    setEquipment(equipment.map((row, idx) => (idx === i ? { ...row, [key]: val } : row)));
  const updateEquipmentAndSave = (i, key, val) =>
    setEquipmentAndSave(equipment.map((row, idx) => (idx === i ? { ...row, [key]: val } : row)));
  const removeEquipment = (i) =>
    setEquipmentAndSave(equipment.filter((_, idx) => idx !== i));
  const pickEquipmentFromRegistry = (i, item) => {
    const number = [item.serial_number, item.inventory_number].filter(Boolean).join(', ');
    const valid_until = item.mo_until || item.mo_until_text || '';
    setEquipmentAndSave(
      equipment.map((row, idx) =>
        idx === i
          ? {
              ...row,
              name: item.name || '',
              number,
              doc_number: item.doc_number || '',
              valid_until,
            }
          : row
      )
    );
  };

  const measurementsMax = schema?.measurements?.max || 4;
  const measurements = Array.isArray(values.measurements) ? values.measurements : [];
  const setMeasurements = (list) => setField('measurements', list);
  const setMeasurementsAndSave = (list) => setFieldAndSave('measurements', list);
  const emptyMeasurement = () => ({
    name: '',
    axes: '',
    otmetka: '',
    pour_date: '',
    indirect: ['', '', '', '', '', ''],
    direct: ['', '', '', '', '', ''],
  });
  const addMeasurement = () =>
    setMeasurementsAndSave([...measurements, emptyMeasurement()]);
  const updateMeasurement = (i, key, val) =>
    setMeasurements(measurements.map((m, idx) => (idx === i ? { ...m, [key]: val } : m)));
  const updateMeasurementAndSave = (i, key, val) =>
    setMeasurementsAndSave(measurements.map((m, idx) => (idx === i ? { ...m, [key]: val } : m)));
  const updateMeasurementValue = (i, group, j, val) =>
    setMeasurements(
      measurements.map((m, idx) => {
        if (idx !== i) return m;
        // массивы, сохранённые старой версией чек-листа, могут быть короче 6 — дополняем
        const arr = Array.isArray(m[group]) ? [...m[group]] : [];
        while (arr.length <= j) arr.push('');
        arr[j] = val;
        return { ...m, [group]: arr };
      })
    );
  const removeMeasurement = (i) =>
    setMeasurementsAndSave(measurements.filter((_, idx) => idx !== i));

  const valueInputStyle = {
    width: '100%',
    padding: '10px 4px',
    fontSize: 15,
    textAlign: 'center',
    border: `1px solid ${T.inputBorder}`,
    borderRadius: 8,
    boxSizing: 'border-box',
    background: '#fff',
    color: T.text,
  };

  const renderValueInput = (m, i, group, j) => (
    <input
      key={j}
      type="text"
      inputMode="decimal"
      autoComplete="off"
      value={(m[group] || [])[j] || ''}
      onChange={(e) => updateMeasurementValue(i, group, j, e.target.value)}
      onBlur={flushSave}
      placeholder="—"
      style={valueInputStyle}
    />
  );

  const photos = Array.isArray(values.scheme_photos) ? values.scheme_photos : [];
  const [photoSheetVisible, setPhotoSheetVisible] = useState(false);
  const [photoUploading, setPhotoUploading] = useState(false);
  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);

  const syncPhotosFromTask = (taskData) => {
    const serverPhotos = taskData?.work_codes?.[idx]?.data?.scheme_photos;
    if (Array.isArray(serverPhotos)) {
      setValues((prev) => ({ ...prev, scheme_photos: serverPhotos }));
    }
  };

  const handlePhotoPicked = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    setPhotoUploading(true);
    try {
      const formData = new FormData();
      files.forEach((f) => formData.append('photos', f));
      const resp = await mobileApi.goszadanieGoUploadPhotos(taskId, idx, formData);
      syncPhotosFromTask(resp.data);
      Toast.show({ icon: 'success', content: 'Сохранено' });
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Ошибка загрузки фото';
      Toast.show({ icon: 'fail', content: String(detail) });
    } finally {
      setPhotoUploading(false);
    }
  };

  const handleDeletePhoto = async (url) => {
    setPhotoUploading(true);
    try {
      const resp = await mobileApi.goszadanieGoDeletePhoto(taskId, idx, url);
      syncPhotosFromTask(resp.data);
      Toast.show({ icon: 'success', content: 'Фото удалено' });
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Ошибка удаления фото';
      Toast.show({ icon: 'fail', content: String(detail) });
    } finally {
      setPhotoUploading(false);
    }
  };

  const [protocolLoading, setProtocolLoading] = useState(false);

  const handleProtocol = async () => {
    setProtocolLoading(true);
    try {
      const resp = await mobileApi.goszadanieGoProtocol(taskId, idx);
      const cd = resp.headers['content-disposition'] || '';
      let filename = 'protocol.docx';
      const m = cd.match(/filename\*=UTF-8''([^;]+)/i);
      if (m) filename = decodeURIComponent(m[1]);
      const blobUrl = window.URL.createObjectURL(
        new Blob([resp.data], {
          type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        })
      );
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Не удалось сформировать протокол';
      Toast.show({ icon: 'fail', content: String(detail) });
    } finally {
      setProtocolLoading(false);
    }
  };

  if (taskQuery.isLoading || checklistsQuery.isLoading) {
    return <Loading style={{ display: 'block', margin: '20px auto' }} />;
  }

  if (!code || !schema) {
    return (
      <Card style={{ ...cardStyle, textAlign: 'center', color: T.hint }}>
        Чек-лист для этого кода не найден
      </Card>
    );
  }

  if (code.cancelled) {
    return (
      <div>
        <Button
          size="mini"
          fill="none"
          onClick={() => navigate(`/test/${taskId}`)}
          style={{ marginBottom: 8 }}
        >
          ← К заданию
        </Button>
        <Card style={cardStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Tag color="danger" style={{ fontSize: 14, padding: '4px 12px', borderRadius: 8 }}>
              {code.code}
            </Tag>
            <div style={{ fontSize: 16, fontWeight: 600, color: T.text, flex: 1, lineHeight: 1.3 }}>
              {schema.title}
            </div>
          </div>
          <div style={{ marginTop: 12, fontSize: 14, color: T.danger, fontWeight: 600 }}>
            Код работы отменён
          </div>
          {code.cancel_reason && (
            <div style={{ marginTop: 6, fontSize: 13, color: T.label, lineHeight: 1.4 }}>
              Причина: {code.cancel_reason}
            </div>
          )}
          {code.cancelled_by && (
            <div style={{ marginTop: 4, fontSize: 12, color: T.hint }}>
              Отменил: {code.cancelled_by}
            </div>
          )}
        </Card>
      </div>
    );
  }

  const renderField = (field) => {
    const val = values[field.key] || '';
    if (field.type === 'static') {
      return (
        <CellField key={field.key} label={field.label}>
          <div style={{ fontSize: 15, color: T.text }}>{field.value}</div>
        </CellField>
      );
    }
    if (field.type === 'date') {
      return (
        <CellField key={field.key} label={field.label}>
          <input
            type="date"
            value={val}
            onChange={(e) => setFieldAndSave(field.key, e.target.value)}
            style={cellDateStyle}
          />
        </CellField>
      );
    }
    if (field.type === 'choice') {
      return (
        <div key={field.key} style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 13, fontWeight: 500, color: T.label, marginBottom: 6 }}>
            {field.label}
          </div>
          <ChoiceBlocks
            value={val}
            options={field.options}
            onChange={(v) => setFieldAndSave(field.key, v)}
          />
        </div>
      );
    }
    return (
      <CellField key={field.key} label={field.label}>
        <CellInput
          value={val}
          onChange={(v) => setField(field.key, v)}
          onBlur={flushSave}
          onEnterPress={blurInput}
          enterKeyHint="done"
          placeholder="—"
          clearable
        />
      </CellField>
    );
  };

  const methodLabel = (text) => (
    <div style={{ fontSize: 13, fontWeight: 500, color: T.label, margin: '10px 0 6px' }}>
      {text}
    </div>
  );

  return (
    <div>
      <Button
        size="mini"
        fill="none"
        onClick={() => navigate(`/test/${taskId}`)}
        style={{ marginBottom: 8 }}
      >
        ← К заданию
      </Button>

      <Card style={cardStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Tag
            color={code.done ? 'success' : 'primary'}
            style={{ fontSize: 14, padding: '4px 12px', borderRadius: 8 }}
          >
            {code.code}
          </Tag>
          <div style={{ fontSize: 16, fontWeight: 600, color: T.text, flex: 1, lineHeight: 1.3 }}>
            {schema.title}
          </div>
        </div>
        {task?.address && (
          <div style={{ fontSize: 13, color: T.label, marginTop: 8, lineHeight: 1.4 }}>
            {task.address}
          </div>
        )}
      </Card>

      <SectionCard title="Общие данные" icon={<FileOutline />}>
        {(schema.fields || []).map(renderField)}
      </SectionCard>

      <SectionCard title="Сведения о применяемом оборудовании" icon={<SetOutline />}>
        {equipment.length === 0 && (
          <div style={{ fontSize: 13, color: T.hint, marginBottom: 10 }}>
            Оборудование не добавлено
          </div>
        )}
        {equipment.map((row, i) => (
          <SubBlock key={i} title={`№ п/п: ${i + 1}`} onDelete={() => removeEquipment(i)}>
            <CellField label="Наименование применяемого оборудования" light>
              <EquipmentNameSearch
                value={row.name || ''}
                onManualChange={(v) => updateEquipment(i, 'name', v)}
                onPick={(item) => pickEquipmentFromRegistry(i, item)}
                onBlur={flushSave}
              />
            </CellField>
            <CellField label="Номер (зав., внутр.)" light>
              <CellInput
                value={row.number || ''}
                onChange={(v) => updateEquipment(i, 'number', v)}
                onBlur={flushSave}
                onEnterPress={blurInput}
                enterKeyHint="done"
                placeholder="—"
                clearable
              />
            </CellField>
            <CellField label="Номер документа о метрологическом обеспечении" light>
              <CellInput
                value={row.doc_number || ''}
                onChange={(v) => updateEquipment(i, 'doc_number', v)}
                onBlur={flushSave}
                onEnterPress={blurInput}
                enterKeyHint="done"
                placeholder="—"
                clearable
              />
            </CellField>
            <CellField label="Срок действия до" light style={{ marginBottom: 0 }}>
              {!row.valid_until || /^\d{4}-\d{2}-\d{2}$/.test(row.valid_until) ? (
                <input
                  type="date"
                  value={row.valid_until || ''}
                  onChange={(e) => updateEquipmentAndSave(i, 'valid_until', e.target.value)}
                  style={cellDateStyle}
                />
              ) : (
                <CellInput
                  value={row.valid_until || ''}
                  onChange={(v) => updateEquipment(i, 'valid_until', v)}
                  onBlur={flushSave}
                  onEnterPress={blurInput}
                  enterKeyHint="done"
                  placeholder="—"
                  clearable
                />
              )}
            </CellField>
          </SubBlock>
        ))}

        <Button block color="primary" fill="outline" onClick={addEquipment}>
          <AddOutline /> Добавить оборудование
        </Button>
      </SectionCard>

      {(schema.extra || []).length > 0 && (
        <SectionCard title="Дополнительные сведения" icon={<InformationCircleOutline />}>
          {(schema.extra || []).map(renderField)}
        </SectionCard>
      )}

      {schema.measurements && (
        <SectionCard title="Результаты измерений" icon={<HistogramOutline />}>
          {measurements.length === 0 && (
            <div style={{ fontSize: 13, color: T.hint, marginBottom: 10 }}>
              Конструкции не добавлены
            </div>
          )}
          {measurements.map((m, i) => (
            <SubBlock key={i} title={`Конструкция ${i + 1}`} onDelete={() => removeMeasurement(i)}>
              <CellField label="Наименование конструкции" light>
                <CellInput
                  value={m.name || ''}
                  onChange={(v) => updateMeasurement(i, 'name', v)}
                  onBlur={flushSave}
                  onEnterPress={blurInput}
                  enterKeyHint="done"
                  placeholder="—"
                  clearable
                />
              </CellField>
              <CellField label="в осях" light>
                <CellInput
                  value={m.axes || ''}
                  onChange={(v) => updateMeasurement(i, 'axes', v)}
                  onBlur={flushSave}
                  onEnterPress={blurInput}
                  enterKeyHint="done"
                  placeholder="—"
                  clearable
                />
              </CellField>
              <CellField label="на отметке" light>
                <CellInput
                  value={m.otmetka || ''}
                  onChange={(v) => updateMeasurement(i, 'otmetka', v)}
                  onBlur={flushSave}
                  onEnterPress={blurInput}
                  enterKeyHint="done"
                  placeholder="—"
                  clearable
                />
              </CellField>
              <CellField label="дата бетонирования" light style={{ marginBottom: 12 }}>
                <input
                  type="date"
                  value={m.pour_date || ''}
                  onChange={(e) => updateMeasurementAndSave(i, 'pour_date', e.target.value)}
                  style={cellDateStyle}
                />
              </CellField>
              {methodLabel('Косвенный метод, м/с')}
              {[0, 3].map((start) => (
                <div key={start} style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                  {[0, 1, 2].map((k) => renderValueInput(m, i, 'indirect', start + k))}
                </div>
              ))}
              {methodLabel('Прямой метод, МПа')}
              {[0, 3].map((start) => (
                <div key={start} style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                  {[0, 1, 2].map((k) => renderValueInput(m, i, 'direct', start + k))}
                </div>
              ))}
            </SubBlock>
          ))}
          {measurements.length < measurementsMax && (
            <Button block color="primary" fill="outline" onClick={addMeasurement}>
              <AddOutline /> Добавить конструкцию
            </Button>
          )}
          {schema.measurements.note && (
            <div style={{ marginTop: 14 }}>{renderField(schema.measurements.note)}</div>
          )}
        </SectionCard>
      )}

      {schema.photos && (
        <SectionCard title={schema.photos.label || 'Фото'} icon={<PicturesOutline />}>
          {photos.length > 0 && (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: 8,
                marginBottom: 10,
              }}
            >
              {photos.map((url) => (
                <div key={url} style={{ position: 'relative' }}>
                  <img
                    src={url}
                    alt="Схема"
                    onClick={() => window.open(url, '_blank')}
                    style={{
                      width: '100%',
                      height: 90,
                      objectFit: 'cover',
                      borderRadius: 10,
                      border: `1px solid ${T.divider}`,
                    }}
                  />
                  <DeleteOutline
                    onClick={() => handleDeletePhoto(url)}
                    style={{
                      position: 'absolute',
                      top: 4,
                      right: 4,
                      fontSize: 14,
                      color: '#fff',
                      background: 'rgba(255,77,79,0.9)',
                      borderRadius: '50%',
                      padding: 3,
                    }}
                  />
                </div>
              ))}
            </div>
          )}
          {photos.length === 0 && (
            <Button
              block
              color="primary"
              fill="outline"
              loading={photoUploading}
              onClick={() => setPhotoSheetVisible(true)}
            >
              <AddOutline /> {schema.photos.label || 'Прикрепить фото'}
            </Button>
          )}
          <ActionSheet
            visible={photoSheetVisible}
            onClose={() => setPhotoSheetVisible(false)}
            onAction={(action) => {
              setPhotoSheetVisible(false);
              if (action.key === 'camera') cameraInputRef.current?.click();
              if (action.key === 'gallery') galleryInputRef.current?.click();
            }}
            actions={[
              { text: 'Снять фото', key: 'camera' },
              { text: 'Выбрать из галереи', key: 'gallery' },
            ]}
          />
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            style={{ display: 'none' }}
            onChange={handlePhotoPicked}
          />
          <input
            ref={galleryInputRef}
            type="file"
            accept="image/*"
            multiple
            style={{ display: 'none' }}
            onChange={handlePhotoPicked}
          />
        </SectionCard>
      )}

      <div style={{ position: 'sticky', bottom: 58, marginTop: 14, zIndex: 20 }}>
        <div
          style={{
            background: '#fff',
            borderRadius: 14,
            boxShadow: '0 2px 10px rgba(0, 0, 0, 0.12)',
            padding: 10,
          }}
        >
          {!code.done ? (
            <Button
              block
              color="success"
              size="large"
              loading={saving}
              onClick={handleMarkDone}
            >
              Отметить выполненным
            </Button>
          ) : (
            <div>
              {schema.protocol && (
                <Button
                  block
                  color="primary"
                  size="large"
                  loading={protocolLoading}
                  onClick={handleProtocol}
                >
                  Сформировать протокол
                </Button>
              )}
              <Button
                block
                color="default"
                size="large"
                style={{ marginTop: schema.protocol ? 8 : 0 }}
                loading={saving}
                onClick={handleUnmarkDone}
              >
                Снять отметку о выполнении
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default TestCodePage;
